/**
 * Sketch-object type registry.
 *
 * Types are defined in `defs/` (one file per type) with
 * {@link defineSketchObjectType} and registered at module load via
 * {@link registerSketchObjectType} — the same load-time side-effect pattern the
 * property getter files use. Adding a new object type = one new file in
 * `defs/` plus its import in `defs/index.ts`.
 *
 * `defineSketchObjectType` erases the per-type param generic by capturing the
 * typed schema in closures, so consumers (tool, layer, panels) work against
 * the uniform {@link RegisteredSketchObjectType} without loose types.
 *
 * Type ids are SCHEMA: they are persisted in `sketchobject_type_id` and
 * namespace every param property (`sketchobject_<typeId>_<param>`). Never
 * rename a released id — stored projects reference it.
 */

import { DIMENSION_CATEGORY_ID, type ObjectDefinitionSpec } from 'shared-types';
import { defaultParamValues, paramInitialProperties, paramValuesFrom, readParamValues, registerParamProperties, unregisterParamProperties, type ParamSchema, type ParamValues, type SketchObjectInitialParam } from './paramSchema';
import {
  DIMENSION_CATEGORY_LABEL,
  SKETCH_OBJECT_CATEGORY_LABELS,
  type ConstraintSpec,
  type SketchObjectAnchorKind,
  type SketchObjectFrame,
  type SketchObjectResizeHandleLayout,
  type SketchObjectTypeDefinition,
  type SketchObjectTypeIcon,
} from './types';
import { transformUnitSegments } from './glyphs/frame';
import { getUnitGlyph } from './glyphs/svgGlyphs';

/** A type definition with its param generic erased — what all consumers see. */
export interface RegisteredSketchObjectType {
  readonly id: string;
  readonly label: string;
  /** A built-in {@link SketchObjectCategory} or an organisation category id. */
  readonly category: string;
  readonly anchor: SketchObjectAnchorKind;
  readonly constraint: ConstraintSpec;
  readonly defaultSize: { width: number; depth: number };
  /** LINE+FREE types drawn as a free two-point run (wall-editor style). */
  readonly drawLikeWall: boolean;
  /** A dimension annotation (see SketchObjectTypeDefinition.dimension). */
  readonly dimension: boolean;
  /** The type's own params (labels, kinds, defaults) — e.g. a tool menu offering them for the next placement. */
  readonly paramSchema: ParamSchema;
  /** Dedicated UI icon; undefined → auto-generated from the glyph (typeIcon.tsx). */
  readonly icon?: SketchObjectTypeIcon;
  /** Coupled-param sync hook (see SketchObjectTypeDefinition.syncParams). */
  readonly syncParams?: (objectId: string, changedKey: string) => void;
  /** Present on organisation-defined (data) types — see custom/customSketchObjectType.ts. */
  readonly custom?: { readonly definitionId: string; readonly spec: ObjectDefinitionSpec; readonly tags: readonly string[]; readonly modelUrl?: string };
  /** False for archived custom types: placed objects still render, the picker leaves them out. */
  readonly listed?: boolean;
  /** Register this type's param properties (called once by registerSketchObjectType). */
  registerProperties(): void;
  /** Drop this type's param properties (runtime re-registration of custom types). */
  unregisterProperties(): void;
  /** Glyph segments for a PLACED object — parametric types read their own params. */
  buildGlyph(frame: SketchObjectFrame, objectId: string): number[];
  /** Glyph segments with default params — ghost previews and library thumbnails. */
  buildDefaultGlyph(frame: SketchObjectFrame): number[];
  /** Extra segments drawn only while selected (empty for most types). */
  buildSelectionGlyph(frame: SketchObjectFrame, objectId: string): number[];
  /** Custom resize-handle placement of a placed run (null → default layout). */
  getResizeHandleLayout(frame: SketchObjectFrame, objectId: string): SketchObjectResizeHandleLayout | null;
  /**
   * The type evaluated for explicit param values (missing ones → defaults)
   * instead of a placed object's properties — comparisons, previews, tests.
   */
  withParams(params: Readonly<Record<string, unknown>>): {
    glyph(frame: SketchObjectFrame): number[];
    selectionGlyph(frame: SketchObjectFrame): number[];
    handles(frame: SketchObjectFrame): SketchObjectResizeHandleLayout | null;
  };
  /** Current param values of a placed object (DXF block dedup keys). */
  getParamValues(objectId: string): Readonly<Record<string, unknown>>;
  /** Param default values as initial properties for annotation creation. */
  initialParamProperties(): SketchObjectInitialParam[];
}

export function defineSketchObjectType<S extends ParamSchema>(def: SketchObjectTypeDefinition<S>): RegisteredSketchObjectType {
  const buildWithParams = (frame: SketchObjectFrame, params: ParamValues<S>): number[] => {
    if (def.glyph.kind === 'svg') {
      const out: number[] = [];
      transformUnitSegments(getUnitGlyph(def.glyph.assetKey, def.glyph.svgSource), frame, out);
      return out;
    }
    return def.glyph.build(frame, params);
  };

  return {
    id: def.id,
    label: def.label,
    category: def.category,
    anchor: def.anchor,
    constraint: def.constraint,
    defaultSize: def.defaultSize,
    drawLikeWall: def.drawLikeWall ?? false,
    dimension: def.dimension ?? false,
    paramSchema: def.params,
    icon: def.icon,
    syncParams: def.syncParams,
    registerProperties: () => registerParamProperties(def.id, def.params),
    unregisterProperties: () => unregisterParamProperties(def.id, def.params),
    buildGlyph: (frame, objectId) => buildWithParams(frame, readParamValues(def.id, def.params, objectId)),
    buildDefaultGlyph: (frame) => buildWithParams(frame, defaultParamValues(def.params)),
    buildSelectionGlyph: (frame, objectId) => def.selectionGlyph?.(frame, readParamValues(def.id, def.params, objectId)) ?? [],
    getResizeHandleLayout: (frame, objectId) => def.resizeHandles?.(frame, readParamValues(def.id, def.params, objectId)) ?? null,
    withParams: (raw) => {
      const params = paramValuesFrom(def.params, raw);
      return {
        glyph: (frame) => buildWithParams(frame, params),
        selectionGlyph: (frame) => def.selectionGlyph?.(frame, params) ?? [],
        handles: (frame) => def.resizeHandles?.(frame, params) ?? null,
      };
    },
    getParamValues: (objectId) => readParamValues(def.id, def.params, objectId),
    initialParamProperties: () => paramInitialProperties(def.id, def.params),
  };
}

const typeRegistry = new Map<string, RegisteredSketchObjectType>();
const changeListeners = new Set<() => void>();
/** Labels of organisation categories (built-ins use SKETCH_OBJECT_CATEGORY_LABELS). */
const customCategoryLabels = new Map<string, string>();

let batchDepth = 0;
let notifyPending = false;

function notifyTypesChanged(): void {
  if (batchDepth > 0) {
    notifyPending = true;
    return;
  }
  for (const listener of changeListeners) listener();
}

/** Run registry changes (a whole library load) with ONE change notification at the end. */
export function batchSketchObjectTypeChanges(apply: () => void): void {
  batchDepth++;
  try {
    apply();
  } finally {
    batchDepth--;
    if (batchDepth === 0 && notifyPending) {
      notifyPending = false;
      notifyTypesChanged();
    }
  }
}

export function registerSketchObjectType(type: RegisteredSketchObjectType): void {
  if (typeRegistry.has(type.id)) return;
  typeRegistry.set(type.id, type);
  type.registerProperties();
  notifyTypesChanged();
}

/**
 * Register or REPLACE a runtime (custom) type — its param properties are
 * re-registered so label/min/max edits of a republished definition apply.
 * Built-in ids are never replaced.
 */
export function upsertSketchObjectType(type: RegisteredSketchObjectType): void {
  const existing = typeRegistry.get(type.id);
  if (existing && !existing.custom) return;
  existing?.unregisterProperties();
  typeRegistry.set(type.id, type);
  type.registerProperties();
  notifyTypesChanged();
}

/** Remove a runtime (custom) type; built-ins stay. */
export function unregisterSketchObjectType(id: string): void {
  const existing = typeRegistry.get(id);
  if (!existing?.custom) return;
  existing.unregisterProperties();
  typeRegistry.delete(id);
  notifyTypesChanged();
}

/** Subscribe to registry changes (custom types added/replaced/removed). Returns the unsubscribe. */
export function onSketchObjectTypesChanged(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function getSketchObjectType(id: string): RegisteredSketchObjectType | null {
  return typeRegistry.get(id) ?? null;
}

export function getAllSketchObjectTypes(): RegisteredSketchObjectType[] {
  return Array.from(typeRegistry.values());
}

/**
 * How the organisation shaped its library (mirror of the server's
 * ObjectLibraryConfig): hidden types, its own categories and type placement.
 */
export interface SketchObjectLibraryConfig {
  hiddenTypeIds: readonly string[];
  categories: ReadonlyArray<{ id: string; label: string; order: number }>;
  typeOrder: ReadonlyArray<{ typeId: string; categoryId: string; order: number }>;
}

/** What the library picker needs beyond the config: whether (and how) the user can manage it. */
export interface SketchObjectLibraryHost {
  canManage: boolean;
  openManager: () => void;
}

const EMPTY_LIBRARY_CONFIG: SketchObjectLibraryConfig = { hiddenTypeIds: [], categories: [], typeOrder: [] };
let libraryConfig: SketchObjectLibraryConfig = EMPTY_LIBRARY_CONFIG;
let libraryHost: SketchObjectLibraryHost | null = null;

/** Apply the organisation's library config (null → defaults). Custom category labels come with it. */
export function setSketchObjectLibraryConfig(config: SketchObjectLibraryConfig | null): void {
  libraryConfig = config ?? EMPTY_LIBRARY_CONFIG;
  customCategoryLabels.clear();
  for (const c of [...libraryConfig.categories].sort((a, b) => a.order - b.order)) customCategoryLabels.set(c.id, c.label);
  notifyTypesChanged();
}

export function getSketchObjectLibraryConfig(): SketchObjectLibraryConfig {
  return libraryConfig;
}

export function setSketchObjectLibraryHost(host: SketchObjectLibraryHost | null): void {
  libraryHost = host;
  notifyTypesChanged();
}

export function getSketchObjectLibraryHost(): SketchObjectLibraryHost | null {
  return libraryHost;
}

export function isSketchObjectTypeHidden(id: string): boolean {
  return libraryConfig.hiddenTypeIds.includes(id);
}

/** The category a type is filed under: the organisation's placement, else its own. */
export function sketchObjectTypeCategory(type: RegisteredSketchObjectType): string {
  return libraryConfig.typeOrder.find((p) => p.typeId === type.id)?.categoryId ?? type.category;
}

export interface SketchObjectLibrarySection {
  category: string;
  label: string;
  types: RegisteredSketchObjectType[];
}

/** Which library: the placeable objects (objects tool) or the dimensions (dimension tool). */
export type SketchObjectLibraryKind = 'objects' | 'dimensions';

/**
 * The library as the organisation shaped it: categories (built-in, then its
 * own, then any unknown) holding their types — placed ones in their order,
 * the rest after them in registration order. Hidden types are left out unless
 * `includeHidden`; archived custom types (`listed: false`) always are.
 * Dimensions form their own library (one fixed category).
 */
export function getSketchObjectLibrary({ includeHidden = false, kind = 'objects' }: { includeHidden?: boolean; kind?: SketchObjectLibraryKind } = {}): SketchObjectLibrarySection[] {
  const placementOrder = new Map(libraryConfig.typeOrder.map((p) => [p.typeId, p.order]));
  const visible = getAllSketchObjectTypes().filter((t) => t.listed !== false && t.dimension === (kind === 'dimensions') && (includeHidden || !isSketchObjectTypeHidden(t.id)));
  const categories = kind === 'dimensions' ? [DIMENSION_CATEGORY_ID] : [...Object.keys(SKETCH_OBJECT_CATEGORY_LABELS), ...customCategoryLabels.keys()];
  for (const t of visible) {
    const category = sketchObjectTypeCategory(t);
    if (!categories.includes(category)) categories.push(category);
  }
  const sections: SketchObjectLibrarySection[] = [];
  for (const category of categories) {
    const types = visible
      .map((type, index) => ({ type, index }))
      .filter(({ type }) => sketchObjectTypeCategory(type) === category)
      .sort((a, b) => (placementOrder.get(a.type.id) ?? Infinity) - (placementOrder.get(b.type.id) ?? Infinity) || a.index - b.index)
      .map(({ type }) => type);
    if (types.length > 0) sections.push({ category, label: getSketchObjectCategoryLabel(category), types });
  }
  return sections;
}

export function getSketchObjectCategoryLabel(category: string): string {
  if (category === DIMENSION_CATEGORY_ID) return DIMENSION_CATEGORY_LABEL;
  return (SKETCH_OBJECT_CATEGORY_LABELS as Readonly<Record<string, string>>)[category] ?? customCategoryLabels.get(category) ?? category;
}
